import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';

interface MobileCarouselState {
  carouselRef: React.RefObject<HTMLDivElement | null>;
  scrollbarRef: React.RefObject<HTMLDivElement | null>;
  showScrollbar: boolean;
  scrollbarThumbStyle: CSSProperties;
  onGalleryPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onGalleryClickCapture: (event: React.MouseEvent<HTMLDivElement>) => void;
  onScrollbarPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
}

const useMobileCarousel = (imageCount: number): MobileCarouselState => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const dragPointerIdRef = useRef<number | null>(null);
  const galleryDragPointerIdRef = useRef<number | null>(null);
  const galleryDragStartXRef = useRef(0);
  const galleryDragStartYRef = useRef(0);
  const galleryDragStartScrollLeftRef = useRef(0);
  const hasDraggedGalleryRef = useRef(false);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbOffset, setThumbOffset] = useState(0);
  const [showScrollbar, setShowScrollbar] = useState(false);

  const updateScrollbar = useCallback(() => {
    const carouselNode = carouselRef.current;

    if (!carouselNode) {
      return;
    }

    const { clientWidth, scrollWidth, scrollLeft } = carouselNode;
    const hasOverflow = scrollWidth > clientWidth + 1;
    setShowScrollbar(hasOverflow);

    if (!hasOverflow) {
      setThumbWidth(0);
      setThumbOffset(0);
      return;
    }

    const ratio = clientWidth / scrollWidth;
    const nextThumbWidth = Math.max(clientWidth * ratio, 44);
    const maxThumbOffset = clientWidth - nextThumbWidth;
    const maxScrollLeft = scrollWidth - clientWidth;
    const nextThumbOffset =
      maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * maxThumbOffset : 0;

    setThumbWidth(nextThumbWidth);
    setThumbOffset(nextThumbOffset);
  }, []);

  const syncScrollToPointer = useCallback((pointerClientX: number) => {
    const carouselNode = carouselRef.current;
    const scrollbarNode = scrollbarRef.current;

    if (!carouselNode || !scrollbarNode) {
      return;
    }

    const trackRect = scrollbarNode.getBoundingClientRect();
    if (trackRect.width <= 0) {
      return;
    }

    const clampedX = Math.min(
      Math.max(pointerClientX - trackRect.left, 0),
      trackRect.width
    );
    const scrollProgress = clampedX / trackRect.width;
    const maxScrollLeft = carouselNode.scrollWidth - carouselNode.clientWidth;
    carouselNode.scrollLeft = scrollProgress * maxScrollLeft;
  }, []);

  const onScrollbarPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      dragPointerIdRef.current = event.pointerId;
      syncScrollToPointer(event.clientX);
    },
    [syncScrollToPointer]
  );

  const onGalleryPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!event.isPrimary) {
        return;
      }

      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      const carouselNode = carouselRef.current;
      if (!carouselNode) {
        return;
      }

      galleryDragPointerIdRef.current = event.pointerId;
      galleryDragStartXRef.current = event.clientX;
      galleryDragStartYRef.current = event.clientY;
      galleryDragStartScrollLeftRef.current = carouselNode.scrollLeft;
      hasDraggedGalleryRef.current = false;
    },
    []
  );

  const onGalleryClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (hasDraggedGalleryRef.current) {
        event.preventDefault();
        event.stopPropagation();
        hasDraggedGalleryRef.current = false;
      }
    },
    []
  );

  useLayoutEffect(() => {
    updateScrollbar();
  }, [imageCount, updateScrollbar]);

  useEffect(() => {
    updateScrollbar();

    const carouselNode = carouselRef.current;
    if (!carouselNode) {
      return;
    }

    carouselNode.addEventListener('scroll', updateScrollbar, { passive: true });
    window.addEventListener('resize', updateScrollbar);

    return () => {
      carouselNode.removeEventListener('scroll', updateScrollbar);
      window.removeEventListener('resize', updateScrollbar);
    };
  }, [updateScrollbar]);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (dragPointerIdRef.current === event.pointerId) {
        event.preventDefault();
        syncScrollToPointer(event.clientX);
        return;
      }

      if (galleryDragPointerIdRef.current !== event.pointerId) {
        return;
      }

      const carouselNode = carouselRef.current;
      if (!carouselNode) {
        return;
      }

      const deltaX = event.clientX - galleryDragStartXRef.current;
      const deltaY = event.clientY - galleryDragStartYRef.current;
      if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
        hasDraggedGalleryRef.current = true;
      }

      if (event.pointerType === 'mouse') {
        event.preventDefault();
        carouselNode.scrollLeft =
          galleryDragStartScrollLeftRef.current - deltaX;
      }
    };

    const onPointerUpOrCancel = (event: PointerEvent) => {
      if (dragPointerIdRef.current === event.pointerId) {
        dragPointerIdRef.current = null;
      }

      if (galleryDragPointerIdRef.current === event.pointerId) {
        const carouselNode = carouselRef.current;
        if (
          carouselNode &&
          Math.abs(
            carouselNode.scrollLeft - galleryDragStartScrollLeftRef.current
          ) > 1
        ) {
          hasDraggedGalleryRef.current = true;
        }

        if (event.type === 'pointercancel') {
          hasDraggedGalleryRef.current = true;
        }

        galleryDragPointerIdRef.current = null;
      }
    };

    const onScroll = () => {
      const carouselNode = carouselRef.current;
      if (!carouselNode || galleryDragPointerIdRef.current === null) {
        return;
      }

      if (
        Math.abs(
          carouselNode.scrollLeft - galleryDragStartScrollLeftRef.current
        ) > 1
      ) {
        hasDraggedGalleryRef.current = true;
      }
    };

    const carouselNode = carouselRef.current;
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUpOrCancel);
    window.addEventListener('pointercancel', onPointerUpOrCancel);
    carouselNode?.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUpOrCancel);
      window.removeEventListener('pointercancel', onPointerUpOrCancel);
      carouselNode?.removeEventListener('scroll', onScroll);
    };
  }, [syncScrollToPointer]);

  const scrollbarThumbStyle = useMemo(
    () => ({
      width: `${thumbWidth}px`,
      transform: `translateX(${thumbOffset}px)`,
    }),
    [thumbOffset, thumbWidth]
  );

  return {
    carouselRef,
    scrollbarRef,
    showScrollbar,
    scrollbarThumbStyle,
    onGalleryPointerDown,
    onGalleryClickCapture,
    onScrollbarPointerDown,
  };
};

export default useMobileCarousel;
